import { Form, message } from "antd";
import useFormErrors from "hooks/useFormErrors";
import {
  useEventDetailsQuery,
  useEventUpdateMutation,
} from "@ticketingplatform/api/dist/event/hooks";
import { useIntl } from "react-intl";
import { useCallback, useMemo } from "react";
import {
  EventStatus,
  EventType,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { generatePath, useNavigate } from "react-router-dom";
import useImageUpload from "hooks/useImageUpload";
import {
  GET_EVENTS,
  GET_EVENT,
  GET_EVENT_OCCURRENCES,
  GET_EVENT_OCCURRENCE,
} from "@ticketingplatform/api/dist/event/queries";
import { ROUTE_PATHS } from "const/paths";
import { useNumberParams } from "hooks/useNumberParams";
import useApolloErrorFields from "hooks/useApolloErrorFields";
import usePrompt from "hooks/usePrompt";
import { TAB_QUERY_NAME } from "components/layout/Tabs/const";
import { RECURRING_EVENT_TAB_NAMES } from "views/RecuringEvents/RecuringEventForm/consts";
import { LOCAL_ID_PREFIX } from "utils/generateLocalId";

import { EventFormProps, EventFormValues } from "../EventForm/types";
import { parseEventDataToFormValues } from "../EventDetails/utils";

export const useEventForm = (): EventFormProps => {
  const { id: eventId } = useNumberParams(true);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { onValuesChange } = useFormErrors(form);
  const intl = useIntl();
  const [updateEvent, { error, loading: updating }] = useEventUpdateMutation({
    evictQueries: [
      GET_EVENTS,
      GET_EVENT,
      GET_EVENT_OCCURRENCES,
      GET_EVENT_OCCURRENCE,
    ],
  });
  const fields = useApolloErrorFields(error, true);
  const { data: eventData, loading } = useEventDetailsQuery(
    useMemo(
      () => ({
        variables: {
          venueInclude: false,
          imageInclude: true,
          seatMapInclude: true,
          ticketPoolsInclude: true,
          ticketPoolsTake: -1,
          presalePasswordsInclude: true,
          presalePasswordsTake: -1,
          discountCodesInclude: true,
          includePreviewId: true,
          discountCodesTake: -1,
          discountCodesFilters: {
            OR: [
              {
                expireAt: null,
              },
              {
                expireAt: {
                  gt: new Date(),
                },
              },
            ],
          },
          id: eventId,
        },
      }),
      [eventId]
    )
  );
  const { uploadImage, isUploadLoading } = useImageUpload();
  const initialValues = useMemo(
    () => parseEventDataToFormValues(eventData),
    [eventData]
  );

  const eventTemplateId = eventData?.event?.eventTemplateId;

  usePrompt(updating || isUploadLoading);

  const onFinish = useCallback(
    async (values: EventFormValues) => {
      const {
        input: {
          image,
          seatMap,
          ticketPools,
          discountCodes,
          presalePasswords,
          status,
          ...input
        },
      } = values;

      try {
        const imageId = await (async () => {
          if (!image) {
            return initialValues?.input?.image ? null : undefined;
          }

          if ("id" in image) {
            return undefined;
          }

          const {
            image: { id },
          } = await uploadImage(image);

          return id;
        })();

        const { data } = await updateEvent({
          variables: {
            id: eventId,
            input: {
              ...input,
              discountCodeIds: discountCodes?.map(({ id }) => id),
              ticketPools: ticketPools
                ? ticketPools.map(
                    ({ id, hasAnyTicketSold: _, ...ticketPoolData }) => ({
                      id: id?.startsWith(LOCAL_ID_PREFIX)
                        ? undefined
                        : Number(id),
                      ...ticketPoolData,
                    })
                  )
                : undefined,
              presalePasswords: presalePasswords
                ? presalePasswords.map(
                    ({ id: _, ...presalePassword }) => presalePassword
                  )
                : undefined,
              status: status || EventStatus.PUBLISHED,
              seatMapId:
                initialValues?.input?.seatMap && !seatMap ? null : seatMap?.id,
              imageId,
            },
          },
        });

        if (!data) {
          return;
        }

        message.success(
          intl.formatMessage({
            defaultMessage: "Event has been updated!",
          })
        );

        navigate(
          eventTemplateId
            ? {
                pathname: generatePath(ROUTE_PATHS.RecuringEventDetails, {
                  id: `${eventTemplateId}`,
                }),
                search: `?${TAB_QUERY_NAME}=${RECURRING_EVENT_TAB_NAMES.events}`,
              }
            : ROUTE_PATHS.EventList,
          {
            state: {
              skipBlock: true,
            },
          }
        );
      } catch {
        message.error(
          intl.formatMessage({
            defaultMessage: "Cannot update event! Check form fields.",
          })
        );
      }
    },
    [
      eventId,
      eventTemplateId,
      initialValues?.input?.image,
      initialValues?.input?.seatMap,
      intl,
      navigate,
      updateEvent,
      uploadImage,
    ]
  );

  return {
    fields,
    eventStatus: eventData?.event?.status,
    eventType: eventData?.event?.type || EventType.TICKETED,
    loading,
    form,
    initialValues,
    onFinish,
    onValuesChange: onValuesChange.onFormValuesChange,
    eventUrlIds: {
      preview: eventData?.event?.previewId,
      eventDetails: eventData?.event?.eventOccuranceId,
    },
  };
};
